import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Button, InputField } from '@rocketlane/ui';
import { NodeViewContent } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SharedConfigurationBar } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-bar';
import { SectionHeading } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-heading';
import {
  FluidSectionCard,
  TextAreaContainer,
  SectionContent,
  SectionDivider,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { ConfigurationBar } from '../../../shared/section-styles';

const StyledForm = styled(Form)`
  margin-bottom: var(--spacing-05);
`;

export const PromptInput = ({
  prompt,
  setPrompt,
  onGenerate,
  onCancel,
  onUndo,
  allowResolve,
  getNodeAsPayload,
  pageType,
  pageTriggerListFilter,
}) => {
  const [form] = Form.useForm<{
    title: string;
    aiContext: string;
    rowCriteria: string;
    filterExclusionCriteria: string;
  }>();
  const [showNext, setShowNext] = useState(!prompt.title?.trim());
  const aiContextRef = useRef<HTMLTextAreaElement>(null);
  const rowCriteriaRef = useRef<HTMLTextAreaElement>(null);
  const filterExclusionCriteriaRef = useRef<HTMLTextAreaElement>(null);

  const contextPlaceholder = useTranslation(
    'rich_text_editor.sections.section.context_placeholder',
  );

  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.extract_info_section.table_title_placeholder',
  );
  const titleRef = useRef<HTMLInputElement>(null);
  const rowCriteriaPlaceholder = useTranslation(
    'rich_text_editor.sections.question_section.row_criteria_placeholder',
  );
  const filterExclusionCriteriaPlaceholder = useTranslation(
    'rich_text_editor.sections.question_section.row_criteria_placeholder',
  );

  useEffect(() => {
    form.setFieldsValue({
      title: prompt.title,
      aiContext: prompt.aiContext,
      rowCriteria: prompt.rowCriteria,
      filterExclusionCriteria: prompt.filterExclusionCriteria,
    });
  }, [
    form,
    prompt.aiContext,
    prompt.rowCriteria,
    prompt.filterExclusionCriteria,
    prompt.title,
  ]);

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.select();
    });
  }, []);

  const triggerGenerate = () => {
    const promptHash = {
      ...prompt,
      title: form.getFieldValue('title'),
      aiContext: form.getFieldValue('aiContext'),
      rowCriteria: form.getFieldValue('rowCriteria'),
      filterExclusionCriteria: form.getFieldValue('filterExclusionCriteria'),
    };

    setPrompt(promptHash);
    onGenerate(promptHash);
  };
  const isDisabled = !prompt.title?.trim();

  return (
    <FluidSectionCard>
      <SectionHeading
        title="editor.sections.record_block_section.title"
        onClose={onUndo}
        showCloseButton={true}
      />
      <SectionContent>
        <StyledForm form={form} layout="vertical">
          <Form.Item
            name="title"
            label={
              <FormattedMessage id="rich_text_editor.sections.question_section.title" />
            }
          >
            <InputField
              type="text"
              ref={titleRef}
              onChange={(e) => {
                setPrompt({
                  ...prompt,
                  title: e.target.value,
                });
              }}
              placeholder={titlePlaceholder}
              autoFocus
            />
          </Form.Item>
          <Form.Item>
            <NodeViewContent />
          </Form.Item>
          {!showNext && (
            <>
              <Form.Item
                name="aiContext"
                label={
                  <FormattedMessage id="rich_text_editor.sections.question_section.add_ai_context" />
                }
              >
                <TextAreaContainer
                  id="aiContext"
                  ref={aiContextRef}
                  value={prompt.aiContext || ''}
                  onChange={(e) =>
                    setPrompt({ ...prompt, aiContext: e.target.value })
                  }
                  placeholder={contextPlaceholder}
                />
              </Form.Item>
              <Form.Item
                name="rowCriteria"
                label={
                  <FormattedMessage id="rich_text_editor.sections.question_section.row_criteria" />
                }
              >
                <TextAreaContainer
                  id="rowCriteria"
                  ref={rowCriteriaRef}
                  value={prompt.rowCriteria || ''}
                  onChange={(e) =>
                    setPrompt({ ...prompt, rowCriteria: e.target.value })
                  }
                  placeholder={rowCriteriaPlaceholder}
                />
              </Form.Item>
              <Form.Item
                name="filterExclusionCriteria"
                label={
                  <FormattedMessage id="rich_text_editor.sections.question_section.filter_exclusion_criteria" />
                }
              >
                <TextAreaContainer
                  id="filterExclusionCriteria"
                  ref={filterExclusionCriteriaRef}
                  value={prompt.filterExclusionCriteria || ''}
                  onChange={(e) =>
                    setPrompt({
                      ...prompt,
                      filterExclusionCriteria: e.target.value,
                    })
                  }
                  placeholder={filterExclusionCriteriaPlaceholder}
                />
              </Form.Item>
            </>
          )}
        </StyledForm>
        {showNext ? (
          <ConfigurationBar>
            <Button
              variant="primary"
              disabled={!prompt.title?.trim()}
              onClick={() => {
                setShowNext(false);
              }}
            >
              <FormattedMessage id="next" />
            </Button>
          </ConfigurationBar>
        ) : (
          <>
            <SectionDivider />
            <SharedConfigurationBar
              prompt={prompt}
              setPrompt={setPrompt}
              onCancel={onCancel}
              onSubmit={triggerGenerate}
              validation={{
                isDisabled,
              }}
              getNodeAsPayload={getNodeAsPayload}
              allowResolve={allowResolve}
              actionBarConfig={{
                canShowPreviewButton: false,
                canWrapTrashButton: true,
                canShowStreaming: true,
              }}
              pageType={pageType}
              pageTriggerListFilter={pageTriggerListFilter}
            />
          </>
        )}
      </SectionContent>
    </FluidSectionCard>
  );
};
